import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContractAgency } from '@core/models/contract-agency.model';
import { ContractUt } from '@core/models/contract-ut.model';
import { ContractAgencyService } from '@core/services/contract-agency/contract-agency.service';
import { ContractUtService } from '@core/services/contract-ut/contract-ut.service';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

@Component({
standalone: false,
  selector: 'app-contract-advertising-center-group',
  templateUrl: './contract-advertising-center-group.component.html',
  styleUrls: ['./contract-advertising-center-group.component.scss'],
})
export class ContractAdvertisingCenterGroupComponent implements OnInit {
  @Input() contractOtherCentralAgency: any;

  advertisingCenterFormGroup: FormGroup;

  // Autocomplete data
  isContractUtLoading = false;

  customerServiceManagerList: ContractUt[] = [];
  filteredCustomerServiceManagerList: Observable<ContractUt[]>;

  commercialAgentList: ContractUt[] = [];
  filteredCommercialAgentList: Observable<ContractUt[]>;

  isCentralAgencyLoading = false;
  otherCentralAgencyList: ContractAgency[] = [];
  filteredOtherCentralAgencyList: Observable<ContractAgency[]>;

  componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private fb: FormBuilder,
    private contractUtService: ContractUtService,
    private contractAgencyService: ContractAgencyService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initAdvertisingCenterForm();
    this.getContractCustomerServiceManagerList();
    this.getContractCommercialAgentList();
    this.getContractAgencies();
  }

  public isFormValid(): boolean {
    return this.advertisingCenterFormGroup.valid;
  }

  public getFormData() {
    return this.advertisingCenterFormGroup.value;
  }

  public markAllAsTouched() {
    this.advertisingCenterFormGroup.markAllAsTouched();
  }

  public cancelFormsModification() {
    this.initForm();
    this.initAdvertisingCenterForm();
  }

  protected initForm() {
    this.advertisingCenterFormGroup = this.fb.group({
      customerServiceManager: [null, Validators.required],
      otherCentralAgency: [null, Validators.required],
      commercialAgent: [null, Validators.required],
    });
  }

  private initAdvertisingCenterForm() {
    let customerServiceManager = this.contractOtherCentralAgency?.customerServiceManager
      ? new ContractUt(this.contractOtherCentralAgency.customerServiceManager)
      : null;

    let otherCentralAgency = this.contractOtherCentralAgency?.centralAgency
      ? new ContractAgency(this.contractOtherCentralAgency.centralAgency)
      : null;

    let commercialAgent = this.contractOtherCentralAgency?.commercialAgent
      ? new ContractUt(this.contractOtherCentralAgency.commercialAgent)
      : null;

    this.advertisingCenterFormGroup.patchValue({
      customerServiceManager: customerServiceManager,
      otherCentralAgency: otherCentralAgency,
      commercialAgent: commercialAgent,
    });
  }

  // --- Load autocomplete data ---

  // customer service manager

  private getContractCustomerServiceManagerList() {
    this.isContractUtLoading = true;

    this.contractUtService
      .getList({ utType: 'customerServiceManager' })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractUtList => {
          this.isContractUtLoading = false;
          this.customerServiceManagerList = contractUtList;

          this.initFilteredCustomerServiceManagerList();
        },
        error => {
          this.isContractUtLoading = false;
        }
      );
  }

  private initFilteredCustomerServiceManagerList() {
    if (!this.isContractUtLoading) {
      this.filteredCustomerServiceManagerList = this.advertisingCenterFormGroup.get('customerServiceManager')?.valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterCustomerServiceManager(label) : this.customerServiceManagerList.slice()))
      );
    }
  }

  private _filterCustomerServiceManager(value: string = ''): ContractUt[] {
    const filterValue = value.toLowerCase();
    return this.customerServiceManagerList.filter(
      customerServiceManager =>
        customerServiceManager.id.toString().toLowerCase().includes(filterValue) ||
        customerServiceManager.name.toLowerCase().includes(filterValue)
    );
  }

  // commercial agent

  private getContractCommercialAgentList() {
    this.isContractUtLoading = true;

    this.contractUtService
      .getList({ utType: 'commercialAgent' })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractUtList => {
          this.isContractUtLoading = false;

          this.customerServiceManagerList = contractUtList;
          this.commercialAgentList = contractUtList;

          this.initFilteredCommercialAgentList();
        },
        error => {
          this.isContractUtLoading = false;
        }
      );
  }

  private initFilteredCommercialAgentList() {
    if (!this.isContractUtLoading) {
      this.filteredCommercialAgentList = this.advertisingCenterFormGroup.get('commercialAgent')?.valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterCommercialAgent(label) : this.commercialAgentList.slice()))
      );
    }
  }

  private _filterCommercialAgent(value: string = ''): ContractUt[] {
    const filterValue = value.toLowerCase();
    return this.commercialAgentList.filter(
      commercialAgent =>
        commercialAgent.id.toString().toLowerCase().includes(filterValue) || commercialAgent.name.toLowerCase().includes(filterValue)
    );
  }

  // --- contract agency ---

  private getContractAgencies() {
    this.isCentralAgencyLoading = true;

    this.contractAgencyService
      .getList({})
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        centralAgencyList => {
          this.isCentralAgencyLoading = false;
          this.otherCentralAgencyList = centralAgencyList;
          this.initFilteredOtherContractAgencyList();
        },
        error => {
          this.isCentralAgencyLoading = false;
        }
      );
  }

  private initFilteredOtherContractAgencyList() {
    if (!this.isCentralAgencyLoading) {
      this.filteredOtherCentralAgencyList = this.advertisingCenterFormGroup.get('otherCentralAgency').valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterOtherCentralAgency(label) : this.otherCentralAgencyList.slice()))
      );
    }
  }

  private _filterOtherCentralAgency(value: string = ''): ContractAgency[] {
    const filterValue = value.toLowerCase();
    return this.otherCentralAgencyList.filter(
      otherCentralAgency =>
        otherCentralAgency.id.toString().toLowerCase().includes(filterValue) || otherCentralAgency.name.toLowerCase().includes(filterValue)
    );
  }

  // --- Utility ---

  private _getLabel(value: string | ContractUt | ContractAgency): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  public displayAutoCompleteData(data) {
    return data?.name && data?.id ? `${data.name} (${data.id})` : data;
  }
}
